"use client";

import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Placeholder } from "@lmn/design-system";
import IconArrowLeft from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconArrowLeft.js";
import { AppConfig } from "@/lib/Types/AppConfig";
import { formatPrice } from "@/lib/Helpers/formatPrice";
import { useDurationTabPrice } from "@/app/api/useDurationTabPrice";
import { useScreenSize } from "@/lib/Hooks/useScreenSize";
import {
  Tab,
  TabLabel,
  TabNavButton,
  TabPrice,
  TabsRow,
  TabsViewport,
} from "./DurationTabsStyles";

// Fixed-size window: we always render (at most) 5 tabs with the active one
// centered. Chevrons slide this window by one duration at a time.
const WINDOW_SIZE = 5;

function formatDelta(diff: number): string {
  // Round first so a sub-€1 difference doesn't render a misleading "+ 0 €".
  const rounded = Math.round(diff);
  if (rounded === 0) return formatPrice(0);
  const sign = rounded > 0 ? "+" : "−";
  return `${sign} ${formatPrice(Math.abs(rounded))}`;
}

interface DurationTabItemProps {
  appConfig: AppConfig;
  duration: number;
  isActive: boolean;
  activePrice: number | null;
  activePriceLoading: boolean;
  /** Gate the price fetch until the tab is worth loading (in / near view). */
  shouldFetch: boolean;
  onSelect: (duration: number) => void;
}

function DurationTabItem({
  appConfig,
  duration,
  isActive,
  activePrice,
  activePriceLoading,
  shouldFetch,
  onSelect,
}: DurationTabItemProps) {
  const { price, isLoading } = useDurationTabPrice(
    appConfig,
    duration,
    shouldFetch,
  );

  // Show the placeholder until the price is fetched: while the query runs, and
  // also before it's even been enabled (tab not yet scrolled into view) so an
  // entering tab never flashes a bare "—". Non-active tabs render a delta vs the
  // active price, so they must also wait for the active price to resolve.
  const loading =
    !shouldFetch ||
    (isActive ? isLoading : isLoading || activePriceLoading);

  let priceContent: ReactNode;
  if (loading) {
    priceContent = <Placeholder width="48px" height="14px" />;
  } else if (isActive) {
    priceContent = price != null ? formatPrice(price) : "—";
  } else if (price == null || activePrice == null) {
    priceContent = "—";
  } else {
    priceContent = formatDelta(price - activePrice);
  }

  return (
    <Tab
      type="button"
      aria-pressed={isActive}
      data-active={isActive ? "true" : undefined}
      $isActive={isActive}
      onClick={() => {
        if (!isActive) onSelect(duration);
      }}
      data-testid={`duration-tab-${duration}`}
      data-duration={duration}
    >
      <TabLabel
        variant="BodySmall"
        forceTag="span"
        margin={0}
        weight="bold"
        color={isActive ? "textPrimaryDefault" : "textPrimary"}
      >
        {duration + 1} {duration + 1 === 1 ? "Tag" : "Tage"}
      </TabLabel>
      <TabPrice
        variant="Caption"
        forceTag="span"
        margin={0}
        color={isActive ? "textPrimaryDefault" : "textSecondary"}
      >
        {priceContent}
      </TabPrice>
    </Tab>
  );
}

interface DurationTabsProps {
  appConfig: AppConfig;
  /** Available stay-length durations (sorted asc, length >= 2). */
  durations: number[];
  /** Currently selected duration (= page-wide `duration` from the URL). */
  activeDuration: number;
  /** Cheapest price for the active duration (delta baseline + active label). */
  activePrice: number | null;
  activePriceLoading: boolean;
  onSelect: (duration: number) => void;
}

export default function DurationTabs({
  appConfig,
  durations,
  activeDuration,
  activePrice,
  activePriceLoading,
  onSelect,
}: DurationTabsProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  // Mobile has no chevrons (they mirror the price calendar's touch-swipe
  // pattern), so there we render the FULL list and let the viewport
  // touch-scroll. Desktop keeps the fixed 5-tab window driven by the chevrons.
  const { isMobile } = useScreenSize();

  const maxStart = Math.max(0, durations.length - WINDOW_SIZE);

  // Window start that puts the active duration in the middle of the 5 slots,
  // clamped so the window never runs past either end of the list.
  const centeredStart = useCallback(() => {
    const activeIndex = durations.indexOf(activeDuration);
    const base =
      activeIndex === -1 ? 0 : activeIndex - Math.floor(WINDOW_SIZE / 2);
    return Math.min(Math.max(0, base), maxStart);
  }, [durations, activeDuration, maxStart]);

  const [windowStart, setWindowStart] = useState(centeredStart);

  // Re-center on the active duration whenever it changes (tab click) or the
  // durations list (re)loads. Chevron clicks change `windowStart` directly and
  // do NOT trigger this, so browsing with the arrows never yanks the window
  // back to the selection.
  const durationsKey = durations.join(",");
  useEffect(() => {
    setWindowStart(centeredStart());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDuration, durationsKey]);

  const shiftWindow = useCallback(
    (direction: "left" | "right") => {
      setWindowStart((start) =>
        Math.min(
          Math.max(0, start + (direction === "left" ? -1 : 1)),
          maxStart,
        ),
      );
    },
    [maxStart],
  );

  const renderedDurations = isMobile
    ? durations
    : durations.slice(windowStart, windowStart + WINDOW_SIZE);
  const canScrollLeft = windowStart > 0;
  const canScrollRight = windowStart < maxStart;

  // Lazy-fetch prices: a tab only fetches its cheapest price once it scrolls
  // into (or near) the viewport. This keeps the initial burst small on mobile,
  // where the full list is rendered — instead of firing one datelist call per
  // duration up front, we fetch what the user actually looks at. The active tab
  // is always fetched (it's centered + is the delta baseline). Sticky: once a
  // duration is seen it stays enabled so react-query's cache serves re-views.
  const [seenDurations, setSeenDurations] = useState<Set<number>>(new Set());
  const renderedKey = renderedDurations.join(",");
  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const justSeen = entries
          .filter((e) => e.isIntersecting)
          .map((e) => Number(e.target.getAttribute("data-duration")));
        if (justSeen.length === 0) return;
        setSeenDurations((prev) => {
          const next = new Set(prev);
          let changed = false;
          for (const d of justSeen) {
            if (!next.has(d)) {
              next.add(d);
              changed = true;
            }
          }
          return changed ? next : prev;
        });
      },
      // Lead by 120px on both sides so a tab starts loading just before it's
      // fully swiped into view (feels instant), scoped to the scroll container.
      { root, rootMargin: "0px 120px", threshold: 0 },
    );
    root
      .querySelectorAll<HTMLElement>("[data-duration]")
      .forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [renderedKey]);

  // Keep the active tab centered within the (touch-)scrollable viewport: on
  // mobile the full list overflows the card, and on desktop the window may be
  // wider than the card too. Rect-based so only the container scrolls (never
  // the page); skips silently when the active tab has been chevroned out of the
  // desktop window.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const activeEl = container.querySelector<HTMLElement>(
      '[data-active="true"]',
    );
    if (!activeEl) return;
    const containerRect = container.getBoundingClientRect();
    const activeRect = activeEl.getBoundingClientRect();
    const delta =
      activeRect.left -
      containerRect.left -
      (container.clientWidth - activeRect.width) / 2;
    container.scrollLeft += delta;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowStart, activeDuration, isMobile, durationsKey]);

  return (
    <TabsRow role="group" aria-label="Reisedauer">
      <TabNavButton
        type="button"
        $disabled={!canScrollLeft}
        disabled={!canScrollLeft}
        aria-label="Kürzere Reisedauer"
        onClick={() => shiftWindow("left")}
      >
        <IconArrowLeft />
      </TabNavButton>

      <TabsViewport ref={scrollRef}>
        {renderedDurations.map((duration) => {
          const isActive = duration === activeDuration;
          return (
            <DurationTabItem
              key={duration}
              appConfig={appConfig}
              duration={duration}
              isActive={isActive}
              activePrice={activePrice}
              activePriceLoading={activePriceLoading}
              shouldFetch={isActive || seenDurations.has(duration)}
              onSelect={onSelect}
            />
          );
        })}
      </TabsViewport>

      <TabNavButton
        type="button"
        $disabled={!canScrollRight}
        disabled={!canScrollRight}
        aria-label="Längere Reisedauer"
        onClick={() => shiftWindow("right")}
      >
        <IconArrowLeft style={{ transform: "rotate(180deg)" }} />
      </TabNavButton>
    </TabsRow>
  );
}
